import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { analyticsApi } from '../../api/analytics.js';
import { Card } from '../ui/Card.jsx';
import { KpiCard } from '../ui/KpiCard.jsx';
import { Skeleton } from '../ui/Skeleton.jsx';
import { Tabs } from '../ui/Tabs.jsx';
import { Badge } from '../ui/Badge.jsx';
import { CostTrendChart } from '../charts/CostTrendChart.jsx';
import { BreakdownBars } from '../charts/BreakdownBars.jsx';
import { fmt } from '../../lib/formatters.js';

const KIND_LABELS = {
  chat: 'Chat',
  voice: 'Voce',
  embedding: 'Embedding (RAG)',
  transcription: 'Trascrizione',
  playground: 'Playground'
};

const METRIC_TABS = [
  { value: 'costUsd', label: 'Costo' },
  { value: 'calls', label: 'Chiamate' },
  { value: 'totalTokens', label: 'Token' }
];

function durationLabel(ms) {
  if (!ms) return '0 s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${s % 60} s`;
}

/**
 * Shared AI analytics sections used by both the Dashboard and the Analytics
 * page. Runs its own overview + cost queries for the given period.
 */
export function AiInsights({ period, showAudit = false }) {
  const [metric, setMetric] = useState('costUsd');

  const overviewQuery = useQuery({ queryKey: ['analytics', 'overview', period], queryFn: () => analyticsApi.overview({ period }) });
  const costQuery = useQuery({ queryKey: ['analytics', 'ai-cost', period], queryFn: () => analyticsApi.aiCost({ period }) });

  const ai = overviewQuery.data?.ai;
  const top = overviewQuery.data?.topProducts || [];
  const audit = overviewQuery.data?.auditActivity || [];
  const daily = costQuery.data?.daily || [];
  const byKind = costQuery.data?.byKind || [];
  const byModel = costQuery.data?.byModel || [];
  const loading = overviewQuery.isLoading || costQuery.isLoading;

  const kindItems = byKind
    .slice()
    .sort((a, b) => b.costUsd - a.costUsd)
    .map((k) => ({
      key: k.kind,
      label: KIND_LABELS[k.kind] || k.kind,
      value: k.costUsd,
      valueLabel: fmt.usd(k.costUsd),
      sub: `${fmt.int(k.calls)} chiamate`
    }));

  const modelItems = byModel.slice(0, 8).map((m) => ({
    key: m.model,
    label: m.model,
    value: m.costUsd,
    valueLabel: fmt.usd(m.costUsd),
    sub: `${fmt.int(m.calls)} chiamate · ${fmt.int(m.totalTokens)} token`
  }));

  return (
    <>
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Conversazioni" loading={loading} value={ai ? fmt.int(ai.conversations) : '-'} sub={ai ? `${fmt.int(ai.messages)} messaggi` : null} />
        <KpiCard label="Costo AI" loading={loading} value={ai ? fmt.usd(ai.costUsd) : '-'} sub={ai ? `${fmt.int(ai.aiCalls)} chiamate` : null} />
        <KpiCard label="Token totali" loading={loading} value={ai ? fmt.int(ai.totalTokens) : '-'} sub={ai ? `Latenza media ${fmt.durationMs(ai.avgLatencyMs)}` : null} />
        <KpiCard label="Tasso di errore" loading={loading} value={ai ? fmt.pct(ai.errorRate) : '-'} sub={ai ? `Durata media sessione ${durationLabel(ai.avgSessionDurationMs)}` : null} />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ink">Andamento AI</h2>
              <p className="text-xs text-ink-muted">Valore giornaliero nel periodo selezionato.</p>
            </div>
            <Tabs value={metric} onChange={setMetric} items={METRIC_TABS} />
          </header>
          {loading ? <Skeleton className="h-64 w-full" /> : <CostTrendChart data={daily} metric={metric} />}
        </Card>

        <Card>
          <header className="mb-4">
            <h2 className="text-base font-semibold text-ink">Ripartizione per tipo</h2>
            <p className="text-xs text-ink-muted">Spesa per tipo di utilizzo AI.</p>
          </header>
          {loading ? <Skeleton className="h-48 w-full" /> : <BreakdownBars items={kindItems} emptyText="Nessun utilizzo nel periodo." />}
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <header className="mb-4">
            <h2 className="text-base font-semibold text-ink">Costo per modello</h2>
            <p className="text-xs text-ink-muted">Quali modelli incidono di più sulla spesa.</p>
          </header>
          {loading ? <Skeleton className="h-48 w-full" /> : <BreakdownBars items={modelItems} emptyText="Nessun modello utilizzato." />}
        </Card>

        <Card>
          <header className="mb-4">
            <h2 className="text-base font-semibold text-ink">Prodotti più richiesti</h2>
            <p className="text-xs text-ink-muted">Dalle risposte dell'assistente AI.</p>
          </header>
          {loading ? (
            <div className="flex flex-col gap-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
          ) : top.length === 0 ? (
            <p className="text-sm text-ink-muted">Nessun prodotto rilevato.</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {top.slice(0, 8).map((item, idx) => (
                <li key={item.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="text-xs font-semibold text-ink-muted">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">{item.name || item.sku || item.id}</p>
                      {item.brand && <p className="truncate text-xs text-ink-muted">{item.brand}</p>}
                    </div>
                  </div>
                  <span className="rounded-full bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand">{fmt.int(item.hits)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </section>

      {showAudit && (
        <Card>
          <header className="mb-4">
            <h2 className="text-base font-semibold text-ink">Attività amministrativa</h2>
            <p className="text-xs text-ink-muted">Azioni sul pannello aggregate per tipo e gravità.</p>
          </header>
          {loading ? <Skeleton className="h-32 w-full" /> : audit.length === 0 ? (
            <p className="text-sm text-ink-muted">Nessuna attività nel periodo.</p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {audit.slice(0, 18).map((row, idx) => (
                <li key={idx} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                  <span className="truncate text-ink">{row.action}</span>
                  <span className="flex items-center gap-2">
                    <Badge tone={row.severity === 'critical' ? 'danger' : row.severity === 'warning' ? 'warning' : row.severity === 'notice' ? 'info' : 'neutral'}>{row.severity}</Badge>
                    <span className="text-xs text-ink-muted">{fmt.int(row.count)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </>
  );
}
